import React, { Component } from 'react';

import PaginationComponent from '../components/PaginationComponent';
import TableStateCard from '../components/TableStateCard';
import { sortClass, nextSortDir, sortAriaSort } from '../utils/table';
import { validateSession } from '../utils/functions';
import { buildPaginationQuery, getPaginatedData, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { hasActiveSearch, getSearchEmptyMessage } from '../utils/tableState';
import { RESTOCKING_PAYMENT_STATUS, PROVIDER_INVOICE_TYPES, PROVIDER_INVOICE_TYPE_LABELS, CURRENCIES, currencySymbol, isAdminOrManager } from '../utils/enums';
import { convertToMoney, getTextColorRestockingPayment } from '../utils/formats';
import { openInvoiceFile } from '../utils/invoiceFile';
import { getSelectedBranchId, getCachedBranches } from '../utils/branch';

var config = process.env;

// Las celdas de dato corto (fecha, monto, estado, tipo) no se parten: partirlas suma un renglón
// a TODA la fila, y en una grilla de facturas eso se paga en cada registro.
const NO_WRAP = { whiteSpace: 'nowrap' };

// Los `material-icons` vienen a 24px y con el padding del botón cada acción ocupaba ~48px: las
// cuatro juntas se llevaban 196px y empujaban la tabla FUERA del contenedor. A 18px con padding
// ajustado entran en ~34px cada una y la grilla vuelve a caber, que es de lo que se trataba.
const ICONO = { fontSize: '18px', lineHeight: 1 };

// Estos modales se renderizan A MANO (`display: block`), sin el JS de Bootstrap. Bootstrap deja
// `.modal { overflow: hidden }` y sólo habilita el scroll en `.modal.show`, clase que pone su JS
// — que acá nunca corre. Resultado: un modal más alto que la pantalla se CORTA, y la rueda del
// mouse scrollea la página de atrás en vez del modal.
//
// El contenedor scrollea, y además el body se topea para que el header y los botones del footer
// queden siempre visibles (patrón de StockTransferComponent).
const MODAL = { display: 'block', background: 'rgba(0,0,0,.4)', overflowY: 'auto' };
const MODAL_BODY = { maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' };
const BOTON_ICONO = { padding: '4px 8px' };

// ── Vista agrupada de una liquidación ────────────────────────────────────────────────────
//
// El proveedor no necesita saber qué día se vendió cada unidad: necesita "2 Catan" en un renglón
// para emitir la factura. Pero la liquidación se GUARDA con una línea por venta a propósito — el
// índice único de `provider_invoice_line` (purchase_id + producto) es el candado que impide
// liquidar dos veces la misma venta. Por eso esto agrupa para MOSTRAR y no toca el dato.
//
// La clave incluye el `unit_cost`: `product_purchase.cost` es un snapshot que se congela en cada
// venta, así que el mismo juego vendido antes y después de un cambio de costo tiene dos costos
// distintos. Colapsarlos en un renglón obligaría a inventar un costo unitario que ninguna venta
// tuvo (y el total dejaría de cerrar). Con dos costos salen dos renglones, que es lo correcto y
// además es visible.
function agruparPorProducto(lineas) {
    const grupos = new Map();
    (lineas || []).forEach((l) => {
        const costo = Number(l.unit_cost || 0);
        const clave = `${l.product_prefix}|${l.product_code}|${l.product_type}|${costo}`;
        const previo = grupos.get(clave);
        const cantidad = Number(l.quantity || 0);
        const total = Number(l.line_total || 0);
        if (previo == null) {
            grupos.set(clave, {
                clave,
                product_name: l.product_name,
                product_code: l.product_code,
                unit_cost: costo,
                quantity: cantidad,
                line_total: total,
                ventas: 1,
                // Se conserva el rango de fechas: agrupar no debería perder CUÁNDO pasó, sólo
                // dejar de repetirlo. Sirve para responder "¿de cuándo es esto?" sin desagrupar.
                primera_venta: l.sale_date,
                ultima_venta: l.sale_date,
            });
            return;
        }
        previo.quantity += cantidad;
        previo.line_total += total;
        previo.ventas += 1;
        if (String(l.sale_date) < String(previo.primera_venta)) previo.primera_venta = l.sale_date;
        if (String(l.sale_date) > String(previo.ultima_venta)) previo.ultima_venta = l.sale_date;
    });
    return Array.from(grupos.values())
        .sort((a, b) => String(a.product_name || a.product_code).localeCompare(String(b.product_name || b.product_code), 'es'));
}

// El texto que se pega en el chat/mail del proveedor. Va agrupado y sin fechas por línea: el
// período está en el encabezado y él factura por producto.
function construirListadoProveedor({ titulo, lineas, total }) {
    const agrupadas = agruparPorProducto(lineas);
    const renglones = agrupadas.map((g) => (
        `${g.quantity} x ${g.product_name || g.product_code}  —  $${convertToMoney(g.unit_cost)} c/u  =  $${convertToMoney(g.line_total)}`
    ));
    const unidades = agrupadas.reduce((acc, g) => acc + g.quantity, 0);
    return [
        titulo,
        '',
        ...renglones,
        '',
        `${agrupadas.length} producto(s) · ${unidades} unidad(es)`,
        `TOTAL: $${convertToMoney(total)}`,
    ].join('\n');
}

// ENI-32 — Facturas de proveedor: es donde vive el estado de pago y de donde sale la deuda.
// Antes "la factura" eran tres campos de texto en la cabecera de cada reposición, así que una
// factura que cubría dos reposiciones se contaba dos veces en la deuda del proveedor.
class TableProviderInvoicesComponent extends Component {

    constructor() {
        super();
        this.state = {
            quantityShowRows: 25,
            currentPage: 1,
            totalItems: 0,
            search: '',
            searchInput: '',
            sortBy: 'id',
            sortDir: 'desc',
            pendingOnly: false,
            data: [],
            providers: [],
            loaded: false,
            role: 0,
            sucursales: [],
            modalAbierto: false,
            editandoId: null,     // null = alta
            form: this.formVacio(),
            guardando: false,
            formError: '',
            // Liquidación de consignados: preview antes de comprometer nada, porque hoy ese
            // listado se arma a mano y es lo que se le pasa al proveedor.
            liqAbierto: false,
            liqProveedor: '',
            liqDesde: '',
            liqHasta: '',
            liqPreview: null,
            liqCargando: false,
            liqError: '',
            liqSugerencia: null,
            // Arranca AGRUPADO porque el propósito del modal es producir el listado que se le pasa
            // al proveedor, y ahí repetir el mismo juego una vez por venta es ruido. El detalle por
            // venta queda a un click: sirve para contestar "¿cuándo se vendió esto?".
            liqAgrupado: true,
            // Modo pago masivo: el flujo que vivía en Reposiciones. Mover el pago acá sin traerlo
            // era un retroceso — se pagaba de a una, con un confirm() suelto y sin ver el total.
            modoPago: false,
            seleccionadas: {},   // id -> factura (se guarda el objeto: al paginar el item se va del state)
            confirmando: false,
            pagando: false,
            copiado: false,
            // Comprobante: el archivo se manda en un request aparte del alta/edición porque va
            // como multipart y el resto del form es JSON.
            archivo: null,
            // Detalle: qué cubre esta factura. Los datos ya estaban persistidos (líneas de la
            // liquidación y reposiciones linkeadas) y `GET /providerInvoice/:id` ya los devolvía,
            // pero no había ninguna pantalla que los pidiera.
            detalle: null,
            detalleCargando: false,
            detalleAgrupado: true,
            detalleLinkId: '',   // id de reposición a colgar de esta factura
            // Resultado de la liquidación recién generada: sin esto el modal se cerraba y el
            // listado sólo se recuperaba si habías clickeado "Copiar" ANTES de confirmar (el
            // preview vuelve vacío después, por el NOT EXISTS).
            liqResultado: null,
        };
    }

    formVacio = () => ({
        providerId: '',
        type: 'purchase',
        invoiceNumber: '',
        invoiceDate: '',
        dueDate: '',
        amount: '',
        currency: 1,
        fileLink: '',
        notes: '',
        tieneArchivo: false,
        nombreArchivo: '',
        tamanioArchivo: 0,
    })

    async componentDidMount() {
        this.fetchData();
        this.fetchProviders();
        this.fetchSucursales();
        let role = await validateSession();
        this.setState({ role: role });
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    headers = () => ({
        'access-token': localStorage.getItem('token'),
        'Content-Type': 'application/json',
    })

    // El nombre de la sucursal sale del caché que llena el navbar, pero ese caché puede estar
    // vacío (sesión nueva, TTL vencido) y ahí la columna mostraba "#1", que no le dice nada a
    // nadie. Se pide la lista si hace falta; si tampoco se puede, queda el id.
    fetchSucursales = () => {
        const cacheadas = getCachedBranches();
        if (cacheadas && cacheadas.length > 0) {
            this.setState({ sucursales: cacheadas });
            return;
        }
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}branch/mine`, { method: 'GET', headers: this.headers() })
            .then((r) => r.json())
            .then((data) => {
                if (!(data.error > 0)) this.setState({ sucursales: data.data || [] });
            })
            .catch(() => { /* la columna cae al id */ });
    }

    fetchProviders = () => {
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}provider/product`, { method: 'GET', headers: this.headers() })
            .then((r) => r.json())
            .then((data) => {
                if (!(data.error > 0)) {
                    this.setState({ providers: data.data || [] });
                }
            })
            .catch(() => { /* el selector queda vacío; el alta avisa que falta proveedor */ });
    }

    fetchData = ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        sortBy = this.state.sortBy,
        sortDir = this.state.sortDir,
        pendingOnly = this.state.pendingOnly,
        showLoader = true,
    } = {}) => {
        if (showLoader) {
            this.setState({ loaded: false });
        }
        // Sin `branchId`, el interceptor no manda el header con "Todas" seleccionado y el backend
        // cae en la sucursal por defecto: las facturas de las otras no se veían y —si la default
        // estaba vacía— la pantalla decía "todavía no hay facturas de proveedor". El backend ya
        // soporta 'all'. Mismo patrón que TableRestockingComponent.
        const query = buildPaginationQuery({
            page, limit, search, sortBy, sortDir,
            extraParams: { branchId: getSelectedBranchId() || undefined },
        });
        const extra = pendingOnly ? '&pendingOnly=1' : '';
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice?${query}${extra}`, { method: 'GET', headers: this.headers() })
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                    // Sin esto la pantalla queda con el loader girando para siempre y el usuario
                    // no tiene forma de reintentar salvo recargar.
                    this.setState({ loaded: true });
                    return;
                }
                const paginatedData = getPaginatedData(data);
                const items = paginatedData.items || [];
                this.setState((prev) => ({
                    data: items,
                    // `seleccionadas` guarda el OBJETO factura (hay que sobrevivir al paginado),
                    // así que queda congelado: tildar una de $7.500, editarla a $9.000 y pagar
                    // asentaba $9.000 mientras la barra, el modal y el resumen copiado seguían
                    // diciendo $7.500 — o sea que el número que el admin aprueba y el que se le
                    // manda al contador no eran el que se pagó. Se remapea contra lo fresco, y se
                    // sueltan las que dejaron de estar pendientes (ya las pagó otro).
                    seleccionadas: Object.keys(prev.seleccionadas).reduce((acc, id) => {
                        const fresca = items.find((i) => String(i.id) === String(id));
                        if (fresca) {
                            if (Number(fresca.payment_status) === 2) acc[id] = fresca;
                        } else {
                            acc[id] = prev.seleccionadas[id];   // está en otra página: se conserva
                        }
                        return acc;
                    }, {}),
                    quantityShowRows: paginatedData.limit || Number(limit),
                    currentPage: paginatedData.page || Number(page),
                    totalItems: paginatedData.total || 0,
                    search,
                    sortBy,
                    sortDir,
                    pendingOnly,
                    loaded: true,
                }));
            })
            .catch(() => {
                alert('No se pudieron cargar las facturas. Revisá la conexión y reintentá.');
                this.setState({ loaded: true });
            });
    }

    abrirAlta = () => this.setState({ modalAbierto: true, editandoId: null, form: this.formVacio(), formError: '', archivo: null });

    // El comprobante viaja aparte del form: multipart, no JSON. Se sube DESPUÉS de que la
    // factura existe, porque el path del objeto se arma con su id.
    subirArchivo = (invoiceId) => {
        if (!this.state.archivo) return Promise.resolve(true);
        const datos = new FormData();
        datos.append('file', this.state.archivo);
        return fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/${invoiceId}/file`, {
            method: 'POST',
            // Sin Content-Type a propósito: lo pone el navegador con el boundary del multipart.
            headers: { 'access-token': localStorage.getItem('token') },
            body: datos,
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) {
                    // La factura ya se guardó; lo que falló es el archivo. Se dice exactamente
                    // eso, para que el usuario no crea que perdió todo lo que cargó.
                    alert(`La factura se guardó, pero el comprobante no se pudo subir:\n${data.message}`);
                    return false;
                }
                return true;
            })
            .catch(() => {
                alert('La factura se guardó, pero el comprobante no se pudo subir. Probá de nuevo desde Editar.');
                return false;
            });
    }

    verComprobante = (item) => openInvoiceFile(`providerInvoice/${item.id}/file`)

    // Revertir un pago. El backend ya lo soportaba (`PATCH /:id/status` con status 2, rol 1) pero
    // no había botón: si cargabas $750.000 en vez de $75.000 y pagabas, "Editar" contestaba
    // "revertí el pago primero" y no existía forma de hacerlo. Quedaba una factura incorregible.
    despagar = (item) => {
        const nombre = item.invoice_number || `#${item.id}`;
        if (!window.confirm(
            `Marcar la factura ${nombre} como PENDIENTE de pago?\n\n`
            + 'OJO: el egreso que se asentó en caja NO se revierte (sacar plata de la caja por una '
            + 'corrección de estado sería peor). Si el pago no existió o fue por otro monto, hacé '
            + 'el ajuste a mano desde Movimientos.',
        )) return;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/${item.id}/status`, {
            method: 'PATCH', headers: this.headers(), body: JSON.stringify({ status: 2 }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) { alert(data.message); return; }
                this.fetchData({ showLoader: false });
            })
            .catch(() => alert('No se pudo cambiar el estado. Revisá la conexión.'));
    }

    // Pagar UNA factura, desde su propia fila. Es lo que hace el encargado: el pago masivo
    // ("Ingresar pago") sigue siendo del admin, así que sin este botón el rol 2 tenía el permiso
    // en el backend y ninguna forma de ejercerlo en pantalla.
    //
    // El botón va en la FILA y no en la barra de arriba porque la acción es sobre ESA factura:
    // un control que edita un dato listado va al lado de su dato.
    //
    // El confirm dice el monto Y la moneda. Pagar asienta un egreso por el importe del
    // comprobante, y una factura en dólares confirmada con un "$" al lado es exactamente cómo se
    // aprueba una transferencia por el número equivocado.
    pagarUna = (item) => {
        const nombre = item.invoice_number || `#${item.id}`;
        const proveedor = item.providerDetails ? item.providerDetails.name : `proveedor #${item.provider_id}`;
        // El backend rechaza con 6218 la factura sin monto. Se avisa ANTES, y diciendo qué hacer:
        // el error genérico dejaba al usuario mirando una factura que "no se deja pagar".
        if (item.amount == null || Number(item.amount) <= 0) {
            alert(
                `La factura ${nombre} no tiene monto cargado, así que no se puede pagar.\n\n`
                + 'Editala y cargá el importe que dice el comprobante.',
            );
            return;
        }
        if (!window.confirm(
            `Marcar como PAGA la factura ${nombre} de ${proveedor}?\n\n`
            + `Monto: ${currencySymbol(item.currency)} ${convertToMoney(item.amount)}\n\n`
            + 'Se asienta el egreso en caja. Revertirlo después es sólo del administrador y NO '
            + 'deshace el asiento.',
        )) return;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/${item.id}/status`, {
            method: 'PATCH', headers: this.headers(), body: JSON.stringify({ status: 1 }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) { alert(data.message); return; }
                this.fetchData({ showLoader: false });
            })
            .catch(() => alert('No se pudo registrar el pago. Revisá la conexión.'));
    }

    // ── #16: qué cubre esta factura ─────────────────────────────────────────────────────
    verDetalle = (item) => {
        this.setState({ detalle: { id: item.id, cabecera: item }, detalleCargando: true });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/${item.id}`,
            { method: 'GET', headers: this.headers() })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) { alert(data.message); this.setState({ detalle: null, detalleCargando: false }); return; }
                this.setState({ detalle: data.data, detalleCargando: false });
            })
            .catch(() => { alert('No se pudo cargar el detalle.'); this.setState({ detalle: null, detalleCargando: false }); });
    }

    cerrarDetalle = () => this.setState({ detalle: null, detalleCargando: false, detalleLinkId: '' })

    // Link / unlink de reposiciones. Los dos endpoints existían y no los llamaba nadie, mientras
    // el error 6219 ("no tiene factura asociada… cargá la factura y linkeásela") prometía
    // exactamente esta acción. Sin esto, una reposición que nació sin factura era irreparable.
    linkearReposicion = () => {
        const { detalle, detalleLinkId } = this.state;
        if (!detalle || String(detalleLinkId).trim() === '') return;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/${detalle.id}/restocking`, {
            method: 'POST', headers: this.headers(), body: JSON.stringify({ restockingId: Number(detalleLinkId) }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) { alert(data.message); return; }
                this.setState({ detalleLinkId: '' });
                this.verDetalle({ id: detalle.id });
                this.fetchData({ showLoader: false });
            })
            .catch(() => alert('No se pudo linkear. Revisá la conexión.'));
    }

    desvincularReposicion = (restockingId) => {
        const { detalle } = this.state;
        if (!window.confirm(`Sacar la reposición ${restockingId} de esta factura?\n\nQueda sin factura: deja de generar deuda hasta que la cuelgues de otra.`)) return;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/restocking/${restockingId}`, {
            method: 'DELETE', headers: this.headers(),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) { alert(data.message); return; }
                this.verDetalle({ id: detalle.id });
                this.fetchData({ showLoader: false });
            })
            .catch(() => alert('No se pudo desvincular. Revisá la conexión.'));
    }

    // La columna de sucursal sólo aparece si hay MÁS DE UNA. Con un solo local repetía "#1" en
    // todas las filas —cero información— y se llevaba ~100px que hacían que la tabla no entrara
    // en el contenedor y las acciones quedaran cortadas. El día que abra la segunda, aparece sola.
    mostrarSucursal = () => (this.state.sucursales || []).length > 1

    nombreSucursal = (branchId) => {
        const encontrada = (this.state.sucursales || []).find((b) => String(b.id) === String(branchId));
        return encontrada ? encontrada.name : `#${branchId}`;
    }

    abrirEdicion = (item) => this.setState({
        modalAbierto: true,
        editandoId: item.id,
        archivo: null,
        form: {
            providerId: item.provider_id,
            type: item.type,
            tieneArchivo: !!item.file_path,
            nombreArchivo: item.file_name || '',
            tamanioArchivo: item.file_size || 0,
            invoiceNumber: item.invoice_number || '',
            invoiceDate: item.invoice_date || '',
            dueDate: item.due_date || '',
            amount: item.amount != null ? String(item.amount) : '',
            currency: item.currency != null ? Number(item.currency) : 1,
            fileLink: item.file_link || '',
            notes: item.notes || '',
        },
        formError: '',
    })

    cerrarModal = () => this.setState({ modalAbierto: false, formError: '', guardando: false })

    setCampo = (campo, valor) => this.setState({ form: { ...this.state.form, [campo]: valor } })

    guardar = () => {
        const { editandoId, form } = this.state;
        const esAlta = editandoId == null;

        if (esAlta && !form.providerId) {
            this.setState({ formError: 'Elegí el proveedor.' });
            return;
        }
        // El monto puede quedar vacío: una liquidación de consignados nace SIN comprobante y se
        // le carga el importe cuando el proveedor factura. Lo que no se acepta es un monto que
        // no sea un número, o negativo.
        if (form.amount !== '' && (Number.isNaN(Number(form.amount)) || Number(form.amount) < 0)) {
            this.setState({ formError: 'El monto tiene que ser un número mayor o igual a 0.' });
            return;
        }

        // Cada envío se numera: si el usuario cierra y reabre el modal mientras una respuesta
        // viaja, la que llega tarde ya no corresponde a lo que hay en pantalla.
        this.envioActual = (this.envioActual || 0) + 1;
        const envio = this.envioActual;
        const vigente = () => this.envioActual === envio;
        this.setState({ guardando: true, formError: '' });

        const body = {
            invoiceNumber: form.invoiceNumber || null,
            invoiceDate: form.invoiceDate || null,
            dueDate: form.dueDate || null,
            amount: form.amount === '' ? null : Number(form.amount),
            currency: Number(form.currency) || 1,
            fileLink: form.fileLink || null,
            notes: form.notes || null,
        };
        if (esAlta) {
            body.providerId = Number(form.providerId);
            body.type = form.type;
        }

        const url = config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'providerInvoice' + (esAlta ? '' : `/${editandoId}`);
        fetch(url, { method: esAlta ? 'POST' : 'PATCH', headers: this.headers(), body: JSON.stringify(body) })
            .then((r) => r.json())
            .then((data) => {
                if (!vigente()) return;
                if (data.error > 0) {
                    this.setState({ guardando: false, formError: data.message || 'No se pudo guardar.' });
                    return;
                }
                // El id de una factura recién creada viene en la respuesta; al editar ya lo teníamos.
                const invoiceId = esAlta ? (data.data && data.data.id) : editandoId;
                const subir = invoiceId ? this.subirArchivo(invoiceId) : Promise.resolve(true);
                return subir.then(() => {
                    this.setState({ modalAbierto: false, guardando: false, archivo: null });
                    this.fetchData({ showLoader: false });
                });
            })
            .catch(() => { if (vigente()) this.setState({ guardando: false, formError: 'No se pudo guardar. Revisá la conexión.' }); });
    }

    montoTexto = (item) => {
        // El símbolo sale de la MONEDA de la factura. Con un '$' fijo, una factura de USD 705
        // se leía como $ 705 y mandaba a transferir cuarenta veces menos.
        const simbolo = currencySymbol(item.currency);
        if (item.amount == null) {
            // Diferenciar "todavía no llegó el comprobante" de "la factura es por cero" — en una
            // liquidación recién generada el calculado es justamente el dato útil.
            return item.calculated_amount != null
                ? <span className="text-muted" title="Todavía sin comprobante del proveedor">≈ {simbolo} {Number(item.calculated_amount).toLocaleString('de-DE')}</span>
                : <span className="text-muted">—</span>;
        }
        const difiere = item.calculated_amount != null
            && Math.abs(Number(item.calculated_amount) - Number(item.amount)) > 0.01;
        return (
            <span>
                {simbolo} {Number(item.amount).toLocaleString('de-DE')}
                {difiere ? (
                    <i className="material-icons ml-1" style={{ fontSize: '14px', verticalAlign: 'middle', color: '#c4183c' }}
                        title={`El sistema había calculado ${simbolo} ${Number(item.calculated_amount).toLocaleString('de-DE')}. Manda el monto de la factura.`}>
                        warning
                    </i>
                ) : null}
            </span>
        );
    }

    drawRow = (item, index) => {
        const pendiente = Number(item.payment_status) === 2;
        return (
            <tr role="row" className="odd" key={item.id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                {this.state.modoPago ? (
                    <td>
                        {pendiente ? (
                            <input type="checkbox" checked={!!this.state.seleccionadas[item.id]}
                                onChange={() => this.toggleSeleccion(item)} />
                        ) : null}
                    </td>
                ) : null}
                <td tabIndex="0" className="sorting_1">{item.id}</td>
                <td>{item.providerDetails ? item.providerDetails.name : `#${item.provider_id}`}</td>
                <td>{item.invoice_number || <span className="text-muted">Sin comprobante</span>}</td>
                <td style={NO_WRAP}>{PROVIDER_INVOICE_TYPE_LABELS[item.type] || item.type}</td>
                {/* `nowrap` en las fechas: '2026-08-08' se partía en dos líneas y estiraba la fila
                    entera, que es de lo que más se nota en una grilla de muchos registros. */}
                <td style={NO_WRAP}>{item.invoice_date || '—'}</td>
                <td style={NO_WRAP}>{item.due_date || '—'}</td>
                <td style={{ textAlign: 'right', ...NO_WRAP }}>{this.montoTexto(item)}</td>
                {/* getTextColorRestockingPayment devuelve un NOMBRE DE CLASE ("text-success"),
                    no un color: va en className. Como valor de `color` el browser lo descarta y
                    la columna sale toda en negro, sin distinguir pagada de pendiente. */}
                {this.mostrarSucursal() ? <td style={NO_WRAP}>{this.nombreSucursal(item.branch_id)}</td> : null}
                <td className={getTextColorRestockingPayment(item.payment_status)} style={NO_WRAP}>
                    {RESTOCKING_PAYMENT_STATUS[item.payment_status]}
                    {/* `paid_at` ya viajaba en el payload y no lo mostraba nadie: sin la fecha,
                        "Pagada" no distingue lo que se pagó ayer de lo de hace un año. */}
                    {!pendiente && item.paid_at ? (
                        <span className="text-muted d-block" style={{ fontSize: '11px' }}>
                            {String(item.paid_at).slice(0, 10)}
                        </span>
                    ) : null}
                    {/* Quién la dio por pagada. Desde que el pago no es exclusivo del admin,
                        "Pagada" sin nombre no alcanza para reconstruir qué pasó. Las facturas
                        pagadas antes de que existiera la columna no lo tienen: ahí no se muestra
                        nada, en vez de un "—" que se lee como un dato. */}
                    {!pendiente && item.paid_by ? (
                        <span className="text-muted d-block" style={{ fontSize: '11px' }} title={item.paid_by}>
                            por {item.paid_by}
                        </span>
                    ) : null}
                </td>
                {/* Cuatro botones de TEXTO en una columna angosta se apilaban uno abajo del otro y
                    la fila quedaba del alto de cuatro renglones. Como iconos en un `btn-group`
                    entran en una sola línea — es el patrón que ya usan Tickets y Reposiciones.
                    El `title` es lo que sostiene la legibilidad: son las mismas cuatro acciones
                    repetidas en cada fila, así que el icono se aprende una vez. */}
                <td style={{ textAlign: 'right', ...NO_WRAP }}>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Acciones de la factura">
                        <button type="button" className="btn btn-white" style={BOTON_ICONO}
                            title="Ver qué cubre esta factura"
                            aria-label="Ver detalle" onClick={() => this.verDetalle(item)}>
                            <i className="material-icons" style={ICONO}>remove_red_eye</i>
                        </button>
                        <button type="button" className="btn btn-white" style={BOTON_ICONO}
                            title="Editar la factura"
                            aria-label="Editar" onClick={() => this.abrirEdicion(item)}>
                            <i className="material-icons" style={ICONO}>edit</i>
                        </button>
                        {/* El archivo subido gana sobre el link viejo: es el que está en nuestro
                            storage y se sirve autenticado. El link queda como fallback para las
                            facturas cargadas antes de que existiera la subida. */}
                        {item.file_path ? (
                            <button type="button" className="btn btn-white" style={BOTON_ICONO}
                                title="Ver el comprobante"
                                aria-label="Ver comprobante" onClick={() => this.verComprobante(item)}>
                                <i className="material-icons" style={ICONO}>attach_file</i>
                            </button>
                        ) : item.file_link ? (
                            <a className="btn btn-white" style={BOTON_ICONO}
                                title="Abrir el link a la factura"
                                aria-label="Abrir link" href={item.file_link} target="_blank" rel="noopener noreferrer">
                                <i className="material-icons" style={ICONO}>link</i>
                            </a>
                        ) : null}
                        {/* En modo pago masivo el control de la fila es el checkbox: dos formas
                            de pagar la misma factura en la misma fila es una de más. */}
                        {pendiente && !this.state.modoPago && isAdminOrManager(this.state.role) ? (
                            <button type="button" className="btn btn-white" style={BOTON_ICONO}
                                title="Marcar como paga (asienta el egreso en caja)"
                                aria-label="Marcar como paga" onClick={() => this.pagarUna(item)}>
                                <i className="material-icons" style={ICONO}>price_check</i>
                            </button>
                        ) : null}
                        {/* Despagar sigue siendo del admin: no revierte el asiento, así que deja
                            la caja debitada y la factura adeudada. El backend lo repite (6234);
                            esconder el botón sin el gate del server sería sólo decoración. */}
                        {!pendiente && this.state.role == 1 ? (
                            <button type="button" className="btn btn-white" style={BOTON_ICONO}
                                title="Marcar como pendiente (el asiento de caja NO se revierte)"
                                aria-label="Despagar" onClick={() => this.despagar(item)}>
                                <i className="material-icons" style={ICONO}>undo</i>
                            </button>
                        ) : null}
                    </div>
                </td>
            </tr>
        );
    }

    renderModal = () => {
        if (!this.state.modalAbierto) return null;
        const esAlta = this.state.editandoId == null;
        const { form } = this.state;
        return (
            <div id="modal-provider-invoice" className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">{esAlta ? 'Nueva factura de proveedor' : `Editar factura #${this.state.editandoId}`}</h4>
                        </div>
                        <div className="modal-body" style={MODAL_BODY}>
                            {esAlta ? (
                                <div className="form-group">
                                    <label>Proveedor</label>
                                    <select name="invoiceProvider" className="form-control" value={form.providerId}
                                        onChange={(e) => this.setCampo('providerId', e.target.value)}>
                                        <option value="">Elegí un proveedor</option>
                                        {this.state.providers.map((p) => (
                                            <option key={p.providerId} value={p.providerId}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : null}
                            {esAlta ? (
                                <div className="form-group">
                                    <label>Tipo</label>
                                    <select name="invoiceType" className="form-control" value={form.type}
                                        onChange={(e) => this.setCampo('type', e.target.value)}>
                                        {PROVIDER_INVOICE_TYPES.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : null}
                            <div className="form-group">
                                <label>Nº de factura</label>
                                <input type="text" name="invoiceNumber" className="form-control" maxLength={45}
                                    value={form.invoiceNumber} onChange={(e) => this.setCampo('invoiceNumber', e.target.value)} />
                                <small className="text-muted">
                                    Se puede dejar vacío si el proveedor todavía no emitió el comprobante.
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Fecha de la factura</label>
                                <input type="date" name="invoiceDate" className="form-control"
                                    value={form.invoiceDate} onChange={(e) => this.setCampo('invoiceDate', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Vence</label>
                                <input type="date" name="dueDate" className="form-control"
                                    value={form.dueDate} onChange={(e) => this.setCampo('dueDate', e.target.value)} />
                                <small className="text-muted">
                                    Si el proveedor tiene plazo de pago cargado y dejás esto vacío, se calcula solo.
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Monto</label>
                                {/* Monto y moneda juntos, en la misma fila: son un solo dato. Separados,
                                    se tipea el número y se sale del form sin mirar el selector. */}
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <select name="invoiceCurrency" className="form-control"
                                            style={{ maxWidth: '110px' }}
                                            value={form.currency}
                                            onChange={(e) => this.setCampo('currency', Number(e.target.value))}>
                                            {CURRENCIES.map((m) => <option key={m.value} value={m.value}>{m.symbol}</option>)}
                                        </select>
                                    </div>
                                    <input type="number" name="invoiceAmount" className="form-control" min="0" step="0.01"
                                        value={form.amount} onChange={(e) => this.setCampo('amount', e.target.value)} />
                                </div>
                                <small className="text-muted">
                                    El monto del comprobante, <b>tal como lo dice la factura</b>: si dice USD 254, va
                                    254 con la moneda en USD — no lo convertido a pesos. La moneda viene de la ficha
                                    del proveedor y se puede cambiar acá si esta factura vino en otra.
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Comprobante</label>
                                {form.tieneArchivo && !this.state.archivo ? (
                                    <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
                                        <span className="badge badge-success">Archivo cargado</span>
                                        <span className="text-muted" style={{ fontSize: '13px' }}>
                                            {form.nombreArchivo}
                                            {/* En KB redondeado, un comprobante chico decía "0 KB". */}
                                            {form.tamanioArchivo
                                                ? ` (${form.tamanioArchivo < 1024
                                                    ? `${form.tamanioArchivo} bytes`
                                                    : `${(form.tamanioArchivo / 1024).toFixed(0)} KB`})`
                                                : ''}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-white"
                                            onClick={() => this.verComprobante({ id: this.state.editandoId })}>Ver</button>
                                    </div>
                                ) : null}
                                <input type="file" name="invoiceFile" className="form-control-file"
                                    accept="application/pdf,image/jpeg,image/png,image/webp"
                                    onChange={(e) => this.setState({ archivo: e.target.files[0] || null })} />
                                <small className="text-muted">
                                    PDF o foto, hasta 10 MB. Queda guardado en el sistema y sólo lo ven los usuarios
                                    con acceso: no es un link público.
                                    {form.tieneArchivo ? ' Si subís otro, reemplaza al actual.' : ''}
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Link al comprobante (opcional)</label>
                                <input type="text" name="invoiceFileLink" className="form-control" maxLength={256}
                                    value={form.fileLink} onChange={(e) => this.setCampo('fileLink', e.target.value)} />
                                <small className="text-muted">
                                    Sólo si el comprobante vive en otro lado y no lo querés subir acá.
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Notas</label>
                                <textarea name="invoiceNotes" className="form-control" rows={2}
                                    value={form.notes} onChange={(e) => this.setCampo('notes', e.target.value)} />
                            </div>
                            {this.state.formError ? <div className="text-danger">{this.state.formError}</div> : null}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white" onClick={this.cerrarModal} disabled={this.state.guardando}>Cancelar</button>
                            <button type="button" className="btn btn-accent" onClick={this.guardar} disabled={this.state.guardando}>
                                {this.state.guardando ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Liquidación de consignados ───────────────────────────────────────────────────────
    abrirLiquidacion = () => this.setState({
        liqAbierto: true, liqProveedor: '', liqDesde: '', liqHasta: '',
        liqPreview: null, liqError: '', liqCargando: false, liqResultado: null,
    })

    cerrarLiquidacion = () => this.setState({
        liqAbierto: false, liqPreview: null, liqError: '', liqSugerencia: null, liqResultado: null,
    })

    // Al elegir el proveedor se propone el período que sigue: desde el día siguiente al fin de su
    // última liquidación hasta hoy. Sin esto el operador tipea el rango cada vez, y ahí es donde
    // se forma el hueco que deja ventas sin liquidar para siempre.
    elegirProveedor = (providerId) => {
        this.setState({ liqProveedor: providerId, liqPreview: null, liqError: '', liqSugerencia: null });
        if (!providerId) return;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/settlement/pending?providerId=${providerId}`,
            { method: 'GET', headers: this.headers() })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) return;
                const s = data.data;
                this.setState({
                    liqSugerencia: s,
                    // Sólo se precarga; el operador puede cambiarlo. Si nunca se le liquidó y no
                    // hay nada pendiente, no se inventa una fecha.
                    liqDesde: s.sugeridoDesde || '',
                    liqHasta: s.sugeridoHasta || '',
                });
            })
            .catch(() => { /* la sugerencia es una ayuda: si falla, se completa a mano */ });
    }

    verPreview = () => {
        const { liqProveedor, liqDesde, liqHasta } = this.state;
        if (!liqProveedor || !liqDesde || !liqHasta) {
            this.setState({ liqError: 'Elegí el proveedor y el período.' });
            return;
        }
        this.setState({ liqCargando: true, liqError: '', liqPreview: null, liqResultado: null });
        const query = `providerId=${liqProveedor}&periodStart=${liqDesde}&periodEnd=${liqHasta}`;
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/settlement/preview?${query}`,
            { method: 'GET', headers: this.headers() })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) {
                    this.setState({ liqCargando: false, liqError: data.message || 'No se pudo calcular.' });
                    return;
                }
                this.setState({ liqCargando: false, liqPreview: data.data });
            })
            .catch(() => this.setState({ liqCargando: false, liqError: 'No se pudo calcular. Revisá la conexión.' }));
    }

    confirmarLiquidacion = () => {
        const { liqProveedor, liqDesde, liqHasta, liqPreview } = this.state;
        if (!liqPreview || liqPreview.lines.length === 0) return;
        if (!window.confirm(`Generar la liquidación de ${liqPreview.provider.name} por $ ${liqPreview.total.toLocaleString('de-DE')}?\n\nQueda como factura pendiente, sin número, hasta que el proveedor mande el comprobante.`)) {
            return;
        }
        this.setState({ liqCargando: true, liqError: '' });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/settlement`, {
            method: 'POST', headers: this.headers(),
            body: JSON.stringify({ providerId: Number(liqProveedor), periodStart: liqDesde, periodEnd: liqHasta }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) {
                    this.setState({ liqCargando: false, liqError: data.message || 'No se pudo generar.' });
                    return;
                }
                // NO se cierra el modal: el listado que se acaba de comprometer sólo se recupera
                // desde acá. Volver a pedir el preview devuelve 0 líneas (el NOT EXISTS ya las ve
                // liquidadas), así que si el usuario no había clickeado "Copiar listado" ANTES de
                // confirmar, el detalle para pasarle al proveedor se perdía.
                this.setState({
                    liqCargando: false,
                    liqResultado: { invoice: data.data, preview: liqPreview },
                });
                this.fetchData({ showLoader: false });
            })
            .catch(() => this.setState({ liqCargando: false, liqError: 'No se pudo generar. Revisá la conexión.' }));
    }

    // El listado que se le pasa al proveedor va SIEMPRE agrupado, sin importar cómo esté la tabla
    // en pantalla: él factura por producto y la fecha de cada venta no le sirve para nada. El
    // período ya viaja en el encabezado, así que la información temporal no se pierde.
    copiarListado = () => {
        const p = this.state.liqResultado ? this.state.liqResultado.preview : this.state.liqPreview;
        if (!p) return;
        const texto = construirListadoProveedor({
            titulo: `Liquidación ${p.provider.name} — ${p.periodStart} a ${p.periodEnd}`,
            lineas: p.lines,
            total: p.total,
        });
        navigator.clipboard.writeText(texto).then(() => alert('Listado copiado. Pegáselo al proveedor para que emita la factura.'));
    }

    // Mismo listado, pero desde el detalle de una liquidación ya generada: es el caso de volver a
    // mandárselo al proveedor días después, cuando el modal del preview ya no existe.
    copiarListadoDetalle = () => {
        const d = this.state.detalle;
        if (d == null || !(d.lines || []).length) return;
        const proveedor = d.providerDetails ? d.providerDetails.name : `#${d.provider_id}`;
        const periodo = d.period_start
            ? ` — ${String(d.period_start).slice(0, 10)} a ${String(d.period_end).slice(0, 10)}`
            : '';
        const texto = construirListadoProveedor({
            titulo: `Liquidación ${proveedor}${periodo}`,
            lineas: d.lines,
            total: d.calculated_amount != null ? d.calculated_amount : d.amount,
        });
        navigator.clipboard.writeText(texto).then(() => alert('Listado copiado. Pegáselo al proveedor para que emita la factura.'));
    }

    renderLiquidacion = () => {
        if (!this.state.liqAbierto) return null;
        const { liqPreview, liqResultado } = this.state;
        const consignantes = this.state.providers.filter((p) => Number(p.is_consignment) === 1);
        return (
            <div id="modal-settlement" className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Liquidar consignados</h4>
                        </div>
                        <div className="modal-body" style={MODAL_BODY}>
                            {consignantes.length === 0 ? (
                                <div className="alert alert-warning py-2">
                                    No hay proveedores marcados como "Trabaja en consignación". Marcalos desde Proveedores.
                                </div>
                            ) : null}
                            <div className="row">
                                <div className="col-md-5 form-group">
                                    <label>Proveedor</label>
                                    <select name="settlementProvider" className="form-control" value={this.state.liqProveedor}
                                        onChange={(e) => this.elegirProveedor(e.target.value)}>
                                        <option value="">Elegí uno</option>
                                        {consignantes.map((p) => (
                                            <option key={p.providerId} value={p.providerId}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3 form-group">
                                    <label>Desde</label>
                                    <input type="date" name="settlementFrom" className="form-control" value={this.state.liqDesde}
                                        onChange={(e) => this.setState({ liqDesde: e.target.value, liqPreview: null, liqResultado: null })} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label>Hasta</label>
                                    <input type="date" name="settlementTo" className="form-control" value={this.state.liqHasta}
                                        onChange={(e) => this.setState({ liqHasta: e.target.value, liqPreview: null, liqResultado: null })} />
                                </div>
                                <div className="col-md-1 form-group d-flex align-items-end">
                                    <button type="button" className="btn btn-white" onClick={this.verPreview} disabled={this.state.liqCargando}>Ver</button>
                                </div>
                            </div>

                            {this.state.liqSugerencia ? (
                                <div className="text-muted mb-2" style={{ fontSize: '13px' }}>
                                    {this.state.liqSugerencia.ultimaLiquidacionHasta
                                        ? <span>Última liquidación hasta el <b>{this.state.liqSugerencia.ultimaLiquidacionHasta}</b>. </span>
                                        : <span>A este proveedor no se le liquidó nunca. </span>}
                                    {this.state.liqSugerencia.pendienteCantidad > 0
                                        ? <span>Tiene <b>{this.state.liqSugerencia.pendienteCantidad} venta(s) sin liquidar</b> por $ {convertToMoney(this.state.liqSugerencia.pendienteTotal)} en total.</span>
                                        : <span>No tiene ventas sin liquidar.</span>}
                                </div>
                            ) : null}

                            {this.state.liqError ? <div className="alert alert-danger py-2">{this.state.liqError}</div> : null}

                            {liqResultado ? (
                                <div className="alert alert-success">
                                    <b>Liquidación generada — factura #{liqResultado.invoice && liqResultado.invoice.id}.</b>
                                    <div style={{ fontSize: '13px', marginTop: '4px' }}>
                                        Queda pendiente de pago, sin número, hasta que el proveedor mande el
                                        comprobante. Copiá el listado y pasáselo para que lo emita — después de
                                        cerrar esto no se vuelve a poder pedir.
                                    </div>
                                    <button type="button" className="btn btn-sm btn-white mt-2" onClick={this.copiarListado}>
                                        Copiar listado para el proveedor
                                    </button>
                                </div>
                            ) : null}

                            {liqPreview && !liqResultado ? (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <b>{liqPreview.lines.length} venta(s) — total $ {liqPreview.total.toLocaleString('de-DE')}</b>
                                        <div className="d-flex align-items-center">
                                            {/* El listado que se copia va agrupado siempre; esto cambia
                                                sólo lo que se ve, para poder chequear las fechas. */}
                                            <div className="btn-group btn-group-sm mr-2" role="group" aria-label="Vista del listado">
                                                <button type="button"
                                                    className={`btn ${this.state.liqAgrupado ? 'btn-accent' : 'btn-white'}`}
                                                    onClick={() => this.setState({ liqAgrupado: true })}>Agrupado</button>
                                                <button type="button"
                                                    className={`btn ${this.state.liqAgrupado ? 'btn-white' : 'btn-accent'}`}
                                                    onClick={() => this.setState({ liqAgrupado: false })}>Por venta</button>
                                            </div>
                                            <button type="button" className="btn btn-sm btn-white" onClick={this.copiarListado}>Copiar listado</button>
                                        </div>
                                    </div>
                                    {/* El período lo elige el operador y nada lo obliga a ser contiguo con
                                        el anterior: liquidar 1-15 y saltar al mes siguiente deja las ventas
                                        del medio huérfanas PARA SIEMPRE, sin alerta. Esto no bloquea; muestra
                                        lo que se está dejando afuera antes de confirmar. */}
                                    {liqPreview.pendientesAntes && liqPreview.pendientesAntes.cantidad > 0 ? (
                                        <div className="alert alert-danger py-2">
                                            <b>Quedan {liqPreview.pendientesAntes.cantidad} venta(s) sin liquidar
                                            ANTES de este período</b>, por $ {convertToMoney(liqPreview.pendientesAntes.total)}
                                            {liqPreview.pendientesAntes.masVieja
                                                ? ` (la más vieja del ${String(liqPreview.pendientesAntes.masVieja).slice(0, 10)})` : ''}.
                                            <div style={{ fontSize: '13px', marginTop: '4px' }}>
                                                Si liquidás sólo este período, esas ventas no se le pagan y no vuelven
                                                a aparecer. Movés la fecha "Desde" hacia atrás para incluirlas.
                                            </div>
                                        </div>
                                    ) : null}

                                    {/* Un costo en 0 no es "gratis": es un producto al que nunca se le cargó
                                        el costo. Sin este aviso se le liquida de menos al proveedor y nadie
                                        se entera hasta que él reclama. */}
                                    {/* Un costo en 0 es un producto al que nunca se le cargó el costo. Esas
                                        líneas NO entran en la liquidación: si entraran, el UNIQUE las dejaría
                                        marcadas como liquidadas a $0 para siempre. Se listan con nombre y fecha
                                        porque son exactamente las que hay que facturarle a mano. Antes esto
                                        trababa el mes entero pidiendo "cargá el costo", una acción que el
                                        sistema no soporta: el costo de la venta es un snapshot inmutable. */}
                                    {liqPreview.sinCosto > 0 ? (
                                        <div className="alert alert-warning py-2">
                                            <b>{liqPreview.sinCosto} venta(s) quedan afuera por tener costo 0.</b>
                                            <div style={{ fontSize: '13px', margin: '4px 0' }}>
                                                Ese costo se congela al vender y no se puede corregir después, así que
                                                estas ventas hay que facturárselas a mano (Nueva factura). Cargale el
                                                costo al producto para que las próximas ventas sí entren.
                                            </div>
                                            <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px' }}>
                                                {(liqPreview.noLiquidables || []).map((l, i) => (
                                                    <div key={`nl-${l.purchase_id}-${l.product_code}-${i}`}>
                                                        {String(l.sale_date).slice(0, 10)} · {l.product_name || l.product_code} · x{l.quantity}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {this.state.liqAgrupado ? (
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr><th>Cant.</th><th>Producto</th><th>Vendido</th><th style={{ textAlign: 'right' }}>Costo</th><th style={{ textAlign: 'right' }}>Total</th></tr>
                                                </thead>
                                                <tbody>
                                                    {agruparPorProducto(liqPreview.lines).map((g) => (
                                                        <tr key={g.clave}>
                                                            <td><b>{g.quantity}</b></td>
                                                            <td>{g.product_name || g.product_code}</td>
                                                            {/* Agrupar no debería tapar CUÁNDO se vendió: si son
                                                                varias, se muestra el rango. */}
                                                            <td style={{ fontSize: '12px' }} className="text-muted">
                                                                {String(g.primera_venta).slice(0, 10)}
                                                                {g.ventas > 1 ? ` a ${String(g.ultima_venta).slice(0, 10)}` : ''}
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>$ {convertToMoney(g.unit_cost)}</td>
                                                            <td style={{ textAlign: 'right' }}>$ {convertToMoney(g.line_total)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr><th>Fecha</th><th>Producto</th><th>Cant.</th><th style={{ textAlign: 'right' }}>Costo</th><th style={{ textAlign: 'right' }}>Total</th></tr>
                                                </thead>
                                                <tbody>
                                                    {liqPreview.lines.map((l, i) => (
                                                        <tr key={`${l.purchase_id}-${l.product_code}-${i}`}>
                                                            <td>{String(l.sale_date).slice(0, 10)}</td>
                                                            <td>{l.product_name || l.product_code}</td>
                                                            <td>{l.quantity}</td>
                                                            <td style={{ textAlign: 'right' }}>$ {l.unit_cost}</td>
                                                            <td style={{ textAlign: 'right' }}>$ {l.line_total}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white" onClick={this.cerrarLiquidacion}>Cerrar</button>
                            {liqResultado ? null : (
                                <button type="button" className="btn btn-accent" onClick={this.confirmarLiquidacion}
                                    disabled={this.state.liqCargando || !liqPreview || liqPreview.lines.length === 0}>
                                    {this.state.liqCargando ? 'Generando…' : 'Generar liquidación'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Pago masivo ──────────────────────────────────────────────────────────────────────
    entrarModoPago = () => this.setState(
        { modoPago: true, seleccionadas: {}, pendingOnly: true },
        () => this.fetchData({ page: 1, pendingOnly: true }),
    )

    salirModoPago = () => this.setState(
        { modoPago: false, seleccionadas: {}, confirmando: false, pendingOnly: false },
        () => this.fetchData({ page: 1, pendingOnly: false }),
    )

    toggleSeleccion = (item) => this.setState((prev) => {
        const seleccionadas = { ...prev.seleccionadas };
        if (seleccionadas[item.id]) delete seleccionadas[item.id];
        else seleccionadas[item.id] = item;
        return { seleccionadas };
    })

    seleccionadasLista = () => Object.values(this.state.seleccionadas)

    // Sólo suma lo PAGABLE. Antes caía a `calculated_amount` para las que no tienen monto, pero
    // esas el backend las rechaza con 6218: el total y el texto que se le manda al contador
    // pedían transferir plata que la operación no iba a mover.
    //
    // Y suma POR MONEDA. Un único total mezclaba pesos con dólares y daba un número que no es
    // nada: ni lo que se transfiere en pesos ni lo que se transfiere en dólares. Este total
    // existe para responder "¿cuánto giro?", así que tiene que haber uno por cada giro.
    totalesPorMoneda = () => {
        const porMoneda = new Map();
        this.seleccionadasLista()
            .filter((f) => f.amount != null && Number(f.amount) > 0)
            .forEach((f) => {
                const moneda = Number(f.currency) || 1;
                const previo = porMoneda.get(moneda) || { currency: moneda, total: 0, cantidad: 0 };
                previo.total += Number(f.amount);
                previo.cantidad += 1;
                porMoneda.set(moneda, previo);
            });
        // Pesos primero: es el caso mayoritario y el que se mira sin leer.
        return Array.from(porMoneda.values()).sort((a, b) => a.currency - b.currency);
    }

    // Texto corto para los lugares donde entra una sola línea ("USD 8.951,41 + $ 16.422").
    totalTexto = () => {
        const totales = this.totalesPorMoneda();
        if (totales.length === 0) return `${currencySymbol(1)} 0`;
        return totales.map((t) => `${currencySymbol(t.currency)} ${convertToMoney(t.total)}`).join(' + ');
    }

    // Las que no tienen monto no se pueden pagar (el backend las rechaza con 6218). Se avisa
    // ANTES de mandar, no después de que la mitad del lote ya movió plata.
    sinMonto = () => this.seleccionadasLista().filter((f) => f.amount == null || Number(f.amount) <= 0)

    copiarResumen = () => {
        // El texto que se le pasa al contador lista SÓLO lo que se va a transferir. Las que no
        // tienen monto se nombran aparte, para que no parezca que se omitieron por error.
        const items = this.seleccionadasLista().filter((f) => f.amount != null && Number(f.amount) > 0);
        const lineas = items.map((f) => {
            const prov = f.providerDetails ? f.providerDetails.name : `#${f.provider_id}`;
            return `- ${prov} · ${f.invoice_number || `factura #${f.id}`}: ${currencySymbol(f.currency)} ${convertToMoney(f.amount)}`;
        });
        const excluidas = this.sinMonto().map((f) => {
            const prov = f.providerDetails ? f.providerDetails.name : `#${f.provider_id}`;
            return `- ${prov} · ${f.invoice_number || `factura #${f.id}`}: SIN MONTO, no se paga`;
        });
        const texto = [
            'Detalle de pago a proveedores:', '', ...lineas, '',
            // Un renglón por moneda: es lo que se transfiere en cada giro.
            ...this.totalesPorMoneda().map((t) => `TOTAL ${currencySymbol(t.currency)}: ${convertToMoney(t.total)}`),
            ...(excluidas.length > 0 ? ['', 'Quedan afuera (falta el importe del comprobante):', ...excluidas] : []),
        ].join('\n');
        if (!navigator.clipboard) {
            alert('El navegador no permite copiar automáticamente. Copiá el detalle del modal a mano.');
            return;
        }
        navigator.clipboard.writeText(texto)
            .then(() => {
                this.setState({ copiado: true });
                setTimeout(() => this.setState({ copiado: false }), 2000);
            })
            .catch(() => alert('No se pudo copiar al portapapeles.'));
    }

    confirmarPagoMasivo = () => {
        const items = this.seleccionadasLista();
        if (items.length === 0) return;
        this.setState({ pagando: true });

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}providerInvoice/status_massive`, {
            method: 'PATCH',
            headers: this.headers(),
            body: JSON.stringify({ invoices: items.map((f) => ({ id: f.id, status: 1 })) }),
        })
            .then((r) => r.json())
            .then((data) => {
                // Respuesta parcial: se dice CUÁLES entraron y cuáles no, en vez de un error
                // global que deja al usuario sin saber qué pasó con las demás.
                if (data.error > 0) {
                    const d = data.data || {};
                    const aplicadas = (d.applied || []).length;
                    const fallidas = d.failed || [];
                    alert(
                        `${aplicadas} factura(s) pagadas.\n\n`
                        + `${fallidas.length} no se pudieron pagar:\n`
                        + fallidas.map((f) => `  · #${f.id}: ${f.message}`).join('\n'),
                    );
                } 
                this.setState({ pagando: false, confirmando: false, seleccionadas: {} });
                this.fetchData({ showLoader: false });
            })
            .catch(() => {
                this.setState({ pagando: false });
                alert('No se pudo completar el pago. Revisá la conexión y reintentá.');
            });
    }

    renderConfirmacionPago = () => {
        if (!this.state.confirmando) return null;
        const items = this.seleccionadasLista();
        const sinMonto = this.sinMonto();
        return (
            <div className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Confirmar pago</h4>
                        </div>
                        <div className="modal-body" style={MODAL_BODY}>
                            {sinMonto.length > 0 ? (
                                <div className="alert alert-warning py-2">
                                    {sinMonto.length} de las seleccionadas no tienen monto cargado y van a
                                    rechazarse. Cargales el importe del comprobante primero.
                                </div>
                            ) : null}
                            <p>Se marcan como pagadas y se registra el gasto en caja:</p>
                            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                <table className="table table-sm table-bordered">
                                    <thead>
                                        <tr><th>Proveedor</th><th>Nº factura</th><th>Vence</th><th style={{ textAlign: 'right' }}>Monto</th></tr>
                                    </thead>
                                    <tbody>
                                        {items.map((f) => (
                                            <tr key={f.id}>
                                                <td>{f.providerDetails ? f.providerDetails.name : `#${f.provider_id}`}</td>
                                                <td>{f.invoice_number || <span className="text-muted">sin comprobante</span>}</td>
                                                <td>{f.due_date || '—'}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {f.amount != null
                                                        ? `${currencySymbol(f.currency)} ${convertToMoney(f.amount)}`
                                                        : <span className="text-danger">sin monto</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                {/* Un total por moneda, cada uno es una transferencia distinta. */}
                                <div>
                                    {this.totalesPorMoneda().map((t) => (
                                        <b key={t.currency} style={{ fontSize: '18px', display: 'block' }}>
                                            Total {currencySymbol(t.currency)}: {convertToMoney(t.total)}
                                            <small className="text-muted ml-2" style={{ fontWeight: 400 }}>
                                                ({t.cantidad} factura{t.cantidad === 1 ? '' : 's'})
                                            </small>
                                        </b>
                                    ))}
                                </div>
                                <button type="button" className="btn btn-sm btn-white" onClick={this.copiarResumen}>
                                    {this.state.copiado ? 'Copiado ✓' : 'Copiar resumen'}
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white" onClick={() => this.setState({ confirmando: false })} disabled={this.state.pagando}>Cancelar</button>
                            <button type="button" className="btn btn-accent" onClick={this.confirmarPagoMasivo} disabled={this.state.pagando}>
                                {this.state.pagando ? 'Pagando…' : `Pagar ${items.length} factura(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Detalle: qué cubre la factura ───────────────────────────────────────────────────
    // `GET /providerInvoice/:id` devuelve `lines` (las ventas de una liquidación de consignados)
    // y `restockings` (las reposiciones de una factura de compra). Ya estaba registrado en
    // enpoints.js y no lo llamaba nadie: los datos estaban guardados y no había cómo verlos.
    renderDetalle = () => {
        const d = this.state.detalle;
        if (d == null) return null;
        const cargando = this.state.detalleCargando;
        const lineas = d.lines || [];
        const reposiciones = d.restockings || [];
        return (
            <div className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">
                                Factura #{d.id}{d.invoice_number ? ` — ${d.invoice_number}` : ''}
                            </h4>
                        </div>
                        <div className="modal-body" style={MODAL_BODY}>
                            {cargando ? <p className="text-muted">Cargando…</p> : (
                                <div>
                                    <div className="row mb-3" style={{ fontSize: '14px' }}>
                                        <div className="col-md-6">
                                            <div><b>Proveedor:</b> {d.providerDetails ? d.providerDetails.name : `#${d.provider_id}`}</div>
                                            <div><b>Tipo:</b> {PROVIDER_INVOICE_TYPE_LABELS[d.type] || d.type}</div>
                                            {d.period_start ? (
                                                <div><b>Período:</b> {String(d.period_start).slice(0, 10)} a {String(d.period_end).slice(0, 10)}</div>
                                            ) : null}
                                        </div>
                                        <div className="col-md-6">
                                            <div><b>Monto:</b> {d.amount != null ? `$ ${convertToMoney(d.amount)}` : <span className="text-muted">sin comprobante</span>}</div>
                                            {d.calculated_amount != null ? (
                                                <div className="text-muted"><b>Calculado por el sistema:</b> $ {convertToMoney(d.calculated_amount)}</div>
                                            ) : null}
                                            <div><b>Estado:</b> <span className={getTextColorRestockingPayment(d.payment_status)}>
                                                {RESTOCKING_PAYMENT_STATUS[d.payment_status]}</span>
                                                {d.paid_at ? ` — ${String(d.paid_at).slice(0, 10)}` : ''}
                                                {d.paid_by ? ` por ${d.paid_by}` : ''}</div>
                                        </div>
                                    </div>

                                    {lineas.length > 0 ? (
                                        <div>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <b>{lineas.length} venta(s) liquidada(s)</b>
                                                <div className="d-flex align-items-center">
                                                    <div className="btn-group btn-group-sm mr-2" role="group" aria-label="Vista del listado">
                                                        <button type="button"
                                                            className={`btn ${this.state.detalleAgrupado ? 'btn-accent' : 'btn-white'}`}
                                                            onClick={() => this.setState({ detalleAgrupado: true })}>Agrupado</button>
                                                        <button type="button"
                                                            className={`btn ${this.state.detalleAgrupado ? 'btn-white' : 'btn-accent'}`}
                                                            onClick={() => this.setState({ detalleAgrupado: false })}>Por venta</button>
                                                    </div>
                                                    {/* Volver a mandarle el listado al proveedor sin tener que
                                                        regenerar nada: antes esto sólo existía en el preview. */}
                                                    <button type="button" className="btn btn-sm btn-white" onClick={this.copiarListadoDetalle}>Copiar listado</button>
                                                </div>
                                            </div>
                                            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                                {this.state.detalleAgrupado ? (
                                                    <table className="table table-sm">
                                                        <thead><tr><th>Cant.</th><th>Producto</th><th>Vendido</th><th style={{ textAlign: 'right' }}>Costo</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                                                        <tbody>
                                                            {agruparPorProducto(lineas).map((g) => (
                                                                <tr key={g.clave}>
                                                                    <td><b>{g.quantity}</b></td>
                                                                    <td>{g.product_name || g.product_code}</td>
                                                                    <td style={{ fontSize: '12px' }} className="text-muted">
                                                                        {String(g.primera_venta).slice(0, 10)}
                                                                        {g.ventas > 1 ? ` a ${String(g.ultima_venta).slice(0, 10)}` : ''}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>$ {convertToMoney(g.unit_cost)}</td>
                                                                    <td style={{ textAlign: 'right' }}>$ {convertToMoney(g.line_total)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <table className="table table-sm">
                                                        <thead><tr><th>Fecha</th><th>Producto</th><th>Cant.</th><th style={{ textAlign: 'right' }}>Costo</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
                                                        <tbody>
                                                            {lineas.map((l) => (
                                                                <tr key={l.id}>
                                                                    <td>{String(l.sale_date).slice(0, 10)}</td>
                                                                    <td>{l.product_name || l.product_code}</td>
                                                                    <td>{l.quantity}</td>
                                                                    <td style={{ textAlign: 'right' }}>$ {convertToMoney(l.unit_cost)}</td>
                                                                    <td style={{ textAlign: 'right' }}>$ {convertToMoney(l.line_total)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}

                                    {reposiciones.length > 0 ? (
                                        <div>
                                            <b>{reposiciones.length} reposición(es) que cubre</b>
                                            <table className="table table-sm">
                                                <thead><tr><th>ID</th><th>Fecha</th><th style={{ textAlign: 'right' }}>Total</th><th></th></tr></thead>
                                                <tbody>
                                                    {reposiciones.map((r) => (
                                                        <tr key={r.id}>
                                                            <td>{r.id}</td>
                                                            <td>{r.date ? String(r.date).slice(0, 10) : '—'}</td>
                                                            <td style={{ textAlign: 'right' }}>$ {convertToMoney(r.totalPrice)}</td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                {this.state.role == 1 ? (
                                                                    <button type="button" className="btn btn-sm btn-white" style={{ fontSize: '11px' }}
                                                                        onClick={() => this.desvincularReposicion(r.id)}>Quitar</button>
                                                                ) : null}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : null}

                                    {lineas.length === 0 && reposiciones.length === 0 ? (
                                        <p className="text-muted">
                                            Esta factura no tiene nada colgado todavía: ninguna reposición linkeada
                                            ni ventas liquidadas. La deuda igual se calcula por su monto.
                                        </p>
                                    ) : null}

                                    {/* Reparación: una reposición que nació sin factura no genera deuda y no
                                        se puede pagar (6219). Este es el único lugar desde el que se arregla. */}
                                    {d.type !== 'consignment_settlement' && this.state.role == 1 ? (
                                        <div className="form-inline mt-3">
                                            <label className="mr-2" style={{ fontSize: '13px' }}>Colgar una reposición (ID):</label>
                                            <input type="number" className="form-control form-control-sm mr-2" style={{ width: '110px' }}
                                                value={this.state.detalleLinkId}
                                                onChange={(e) => this.setState({ detalleLinkId: e.target.value })} />
                                            <button type="button" className="btn btn-sm btn-white" onClick={this.linkearReposicion}
                                                disabled={String(this.state.detalleLinkId).trim() === ''}>Linkear</button>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-white" onClick={this.cerrarDetalle}>Cerrar</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    loadData = () => {
        const items = this.state.data.map((item, index) => this.drawRow(item, index));
        if (items.length === 0) {
            items.push(
                <tr key="empty"><td colSpan={(this.state.modoPago ? 10 : 9) + (this.mostrarSucursal() ? 1 : 0)} style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                    {/* Sin este caso, filtrar por pendientes con todo pago mostraba "No se
                        encontraron resultados para tu búsqueda" sin que hubiera búsqueda — y
                        justamente "no hay pendientes" es la buena noticia. */}
                    {this.state.pendingOnly && !this.state.search
                        ? 'No hay facturas pendientes de pago.'
                        : getSearchEmptyMessage(this.state.searchInput || this.state.search)}
                </td></tr>
            );
        }
        return <tbody>{items}</tbody>;
    }

    onChangeShowRows = () => {
        const itemSelected = Number(document.getElementById('select_rows').value);
        this.fetchData({ page: 1, limit: itemSelected });
    }

    onChangeInput = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchData({ page: 1, search, showLoader: false });
        });
    }

    orderData = (column) => {
        this.fetchData({
            page: 1,
            sortBy: column,
            sortDir: nextSortDir(column, this.state.sortBy, this.state.sortDir),
        });
    }

    sortableHeader = (column) => ({
        onClick: () => this.orderData(column),
        className: sortClass(column, this.state.sortBy, this.state.sortDir),
        'aria-sort': sortAriaSort(column, this.state.sortBy, this.state.sortDir),
        tabIndex: '0',
    })

    render() {
        const searchActive = hasActiveSearch(this.state.searchInput, this.state.search);

        if (!this.state.loaded) {
            return <TableStateCard loading />;
        }

        // El botón de alta va también en el estado vacío: si no, sin facturas cargadas no habría
        // forma de crear la primera.
        const seleccion = this.seleccionadasLista();
        const acciones = this.state.modoPago ? (
            // En modo pago la barra cambia entera: lo único que importa es cuánto se va a pagar.
            <div className="card card-small mb-3">
                <div className="card-body py-3 d-flex flex-wrap align-items-center justify-content-between">
                    <div>
                        <span className="text-muted d-block" style={{ fontSize: '12px' }}>
                            {seleccion.length} factura(s) seleccionada(s)
                        </span>
                        <span style={{ fontSize: '24px', fontWeight: 500 }}>
                            {this.totalTexto()}
                        </span>
                    </div>
                    <div>
                        <button type="button" className="btn btn-white mr-2" onClick={this.salirModoPago}>Salir</button>
                        <button type="button" className="btn btn-accent"
                            disabled={seleccion.length === 0}
                            onClick={() => this.setState({ confirmando: true })}>
                            Ingresar pago
                        </button>
                    </div>
                </div>
            </div>
        ) : (
            // Acciones a la izquierda, filtro a la derecha. Mezclados en una sola tira, el
            // toggle de "solo pendientes" se leía como una acción más: los tres primeros HACEN
            // algo (abren un flujo), el último sólo cambia QUÉ ESTÁS MIRANDO.
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: '8px' }}>
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                    <button type="button" className="btn btn-sm btn-accent" onClick={this.abrirAlta}>+ Nueva factura</button>
                    {/* Un solo botón lleno: con dos, ninguno de los dos era "el" principal y la
                        barra gritaba entera. Estos dos siguen perfectamente visibles. */}
                    {this.state.role == 1 ? (
                        <button type="button" className="btn btn-sm btn-white" onClick={this.entrarModoPago}>
                            Ingresar pago
                        </button>
                    ) : null}
                    {this.state.role == 1 ? (
                        <button type="button" className="btn btn-sm btn-white" onClick={this.abrirLiquidacion}>
                            Liquidar consignados
                        </button>
                    ) : null}
                </div>
                {/* Segmentado y no un toggle suelto: mostrar las DOS opciones es lo que hace obvio
                    que es un filtro y en qué estado está. Un botón solo obliga a leer su texto
                    para saber si estás viendo todo o no. */}
                <div className="btn-group btn-group-sm" role="group" aria-label="Filtrar por estado de pago">
                    <button type="button" aria-pressed={this.state.pendingOnly}
                        className={`btn ${this.state.pendingOnly ? 'btn-primary' : 'btn-white'}`}
                        onClick={() => this.fetchData({ page: 1, pendingOnly: true })}>
                        Pendientes
                    </button>
                    <button type="button" aria-pressed={!this.state.pendingOnly}
                        className={`btn ${!this.state.pendingOnly ? 'btn-primary' : 'btn-white'}`}
                        onClick={() => this.fetchData({ page: 1, pendingOnly: false })}>
                        Todas
                    </button>
                </div>
            </div>
        );

        if (this.state.totalItems === 0 && !searchActive && !this.state.pendingOnly) {
            return (
                <div>
                    {acciones}
                    <TableStateCard
                        icon="receipt_long"
                        title="Todavia no hay facturas de proveedor"
                        message="Cargá la factura que te manda el proveedor y despues linkeale las reposiciones que cubre. La deuda de cada proveedor sale de las facturas pendientes."
                    />
                    {this.renderModal()}
                    {this.renderLiquidacion()}
                    {this.renderDetalle()}
                </div>
            );
        }

        return (
            <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                {acciones}
                <div className="dataTables_length" id="DataTables_Table_0_length">
                    <label>Show
                    <select id="select_rows" name="DataTables_Table_0_length" aria-controls="DataTables_Table_0" value={this.state.quantityShowRows} onChange={() => this.onChangeShowRows()}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    entries</label>
                </div>
                <div id="DataTables_Table_0_filter" className="dataTables_filter">
                    <label>Search:
                    <input id="input_search" type="search" placeholder="" aria-controls="DataTables_Table_0" value={this.state.searchInput} onChange={(e) => this.onChangeInput(e.target.value)}/>
                    </label>
                </div>

                <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid">
                    <thead>
                        <tr role="row">
                            {this.state.modoPago ? <th className="sorting_disabled" style={{ width: '4%' }}></th> : null}
                            {/* Anchos rebalanceados: con las etiquetas cortas, Tipo y Estado ya no
                                necesitan lo que ocupaban, y el espacio se va al proveedor, que es
                                el único texto libre y largo de la fila. */}
                            <th {...this.sortableHeader('id')} rowSpan="1" colSpan="1" style={{ width: '5%' }}>ID</th>
                            <th className="sorting_disabled" rowSpan="1" colSpan="1" style={{ width: '22%' }}>Proveedor</th>
                            <th {...this.sortableHeader('invoice_number')} rowSpan="1" colSpan="1" style={{ width: '13%' }}>Nº factura</th>
                            <th className="sorting_disabled" rowSpan="1" colSpan="1" style={{ width: '9%' }}>Tipo</th>
                            <th {...this.sortableHeader('invoice_date')} rowSpan="1" colSpan="1" style={{ width: '9%' }}>Fecha</th>
                            <th {...this.sortableHeader('due_date')} rowSpan="1" colSpan="1" style={{ width: '9%' }}>Vence</th>
                            <th {...this.sortableHeader('amount')} rowSpan="1" colSpan="1" style={{ width: '10%' }}>Monto</th>
                            {this.mostrarSucursal()
                                ? <th className="sorting_disabled" rowSpan="1" colSpan="1" style={{ width: '8%' }}>Sucursal</th>
                                : null}
                            <th {...this.sortableHeader('payment_status')} rowSpan="1" colSpan="1" style={{ width: '8%' }}>Estado</th>
                            <th className="sorting_disabled" rowSpan="1" colSpan="1" style={{ width: '7%' }}></th>
                        </tr>
                    </thead>
                    {this.loadData()}
                </table>

                <PaginationComponent
                    totalItems={this.state.totalItems}
                    quantityRows={this.state.quantityShowRows}
                    currentPage={this.state.currentPage}
                    onPageChange={(page) => this.fetchData({ page })}
                />
                {this.renderModal()}
                {this.renderLiquidacion()}
                {this.renderConfirmacionPago()}
                {this.renderDetalle()}
            </div>
        );
    }
}

export default TableProviderInvoicesComponent;
